import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faHome, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Badge, Breadcrumb, Button, Card, Col, Form, InputGroup, Pagination, Row, Table } from "@themesberg/react-bootstrap";
import { useHistory } from "react-router-dom";

import { getOrders } from "../api/orders.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";

const PAGE_SIZE = 10;

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const STATUS_VARIANTS = {
  pending: { bg: "secondary", text: "Pending" },
  completed: { bg: "warning", text: "Completed" },
  acknowledged: { bg: "success", text: "Acknowledged" },
  rejected: { bg: "danger", text: "Rejected" },
  cancelled: { bg: "dark", text: "Cancelled" }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString();
};

const getStatusBadge = (status) => {
  const normalizedStatus = normalizeStatus(status);
  const config = STATUS_VARIANTS[normalizedStatus] || { bg: "light", text: status || "N/A" };

  return (
    <Badge bg={config.bg} className="px-3 py-2 text-uppercase small">
      {config.text}
    </Badge>
  );
};

const OrderTableSkeleton = () => (
  <>
    {[...Array(PAGE_SIZE)].map((_, index) => (
      <tr key={index}>
        <td><div className="skeleton-text" style={{ width: "20px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "50px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "120px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "160px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "80px", height: "20px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "60px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "100px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "70px", height: "32px" }} /></td>
      </tr>
    ))}
  </>
);

export default () => {
  const history = useHistory();
  const { showToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);

      try {
        const response = await getOrders({ page, limit: PAGE_SIZE, search: searchQuery });
        setOrders(response.data || []);
        setPagination(response.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      } catch (error) {
        showToast({ message: error.message || "Failed to load orders", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [page, searchQuery, showToast]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSearchQuery(searchInput);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item active>Orders</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Orders</h4>
          <p className="mb-0">Review all vendor orders from one place.</p>
        </div>
      </div>

      <div className="table-settings mb-4">
        <Row className="justify-content-between align-items-center">
          <Col xs={12} md={6} lg={4}>
            <Form onSubmit={handleSearchSubmit}>
              <InputGroup>
                <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search orders"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
                <Button type="submit" variant="primary">Search</Button>
              </InputGroup>
            </Form>
          </Col>
        </Row>
      </div>
      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="pt-0">
          <Table hover className="user-table align-items-center">
            <thead>
              <tr>
                <th className="border-bottom">#</th>
                <th className="border-bottom">Order ID</th>
                <th className="border-bottom">Vendor</th>
                <th className="border-bottom">Email</th>
                <th className="border-bottom">Status</th>
                <th className="border-bottom">Total</th>
                <th className="border-bottom">Created</th>
                <th className="border-bottom">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <OrderTableSkeleton />
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-5">No orders found.</td>
                </tr>
              ) : (
                orders.map((order, index) => (
                  <tr key={order.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>#{order.id}</td>
                    <td>{order.vendor_name || "N/A"}</td>
                    <td>{order.vendor_email || "N/A"}</td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td>${Number(order.total_amount || 0).toFixed(2)}</td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => history.push(Routes.OrderDetails.path.replace(":id", order.id))}
                        >
                          <FontAwesomeIcon icon={faEye} className="me-2" />
                          Details
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <Card.Footer className="px-3 border-0">
            <div className="d-lg-flex align-items-center justify-content-between">
              <Pagination className="mb-2 mb-lg-0">
                <Pagination.Prev disabled={loading || pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>Previous</Pagination.Prev>
                <Pagination.Item active>{pagination.page}</Pagination.Item>
                <Pagination.Next disabled={loading || pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next</Pagination.Next>
              </Pagination>
              <small className="fw-bold">
                Showing <b>{orders.length}</b> of <b>{pagination.total}</b> orders
              </small>
            </div>
          </Card.Footer>
        </Card.Body>
      </Card>
    </>
  );
};
